import { isNil } from 'lodash';
import { nameOf } from '~domain/helper/objects';
import { PatternValidationRegex } from '~domain/helper/validation-regex';
import { Address } from '~domain/model/application/home/address';
import { Coordinates } from '~domain/model/core/address/coordinates';
import { ErrorMessage, Rule } from '~domain/model/core/validation/rule';
import { BaseValidator } from '~domain/validators/core/base-validator';
import { CityValidator } from '~domain/validators/core/basic-types/address/city-validator';
import { LatitudeValidator } from '~domain/validators/core/basic-types/address/latitude-validator';
import { LongitudeValidator } from '~domain/validators/core/basic-types/address/longitude-validator';
import { ProvinceValidator } from '~domain/validators/core/basic-types/address/province-validator';
import { StreetNameValidator } from '~domain/validators/core/basic-types/address/street-name-validator';
import { StreetNumberValidator } from '~domain/validators/core/basic-types/address/street-number-validator';
import { UnitValidator } from '~domain/validators/core/basic-types/address/unit-validator';
import { RegexValidator } from '~domain/validators/core/basic-types/regex-validator';
import { DEFAULT_COUNTRY } from '~domain/variables/variables';

// Validate address fields
export class AddressValidator extends BaseValidator<Address> {
  protected rules: Rule<Address>[] = [
    this.createNullRule(this.createErrorMessage()),
    this.createObjectRule(this.createErrorMessage()),
    this.validateContent,
  ];

  // City field validator
  private cityValidator: CityValidator = new CityValidator();
  // Latitude field validator
  private latitudeValidator: LatitudeValidator = new LatitudeValidator();
  // Longitude field validator
  private longitudeValidator: LongitudeValidator = new LongitudeValidator();
  // Postal code field validator
  private postalCodeValidator: RegexValidator = new RegexValidator(
    PatternValidationRegex.POSTAL_CODE_CANADA_REGEX,
    'validation.error.validPostalCode',
  );

  // Province field validator
  private provinceValidator: ProvinceValidator = new ProvinceValidator();
  // Street name field validator
  private streetNameValidator: StreetNameValidator = new StreetNameValidator();
  // Street number field validator
  private streetNumberValidator: StreetNumberValidator = new StreetNumberValidator();
  // Unit field validator
  private unitValidator: UnitValidator = new UnitValidator();

  // Display error in each field if null or empty
  private createErrorMessage(): ErrorMessage {
    const errors: ErrorMessage = {};
    errors[nameOf((a: Address) => a.streetNumber)] = 'validation.error.required';
    errors[nameOf((a: Address) => a.streetName)] = 'validation.error.required';
    errors[nameOf((a: Address) => a.city)] = 'validation.error.required';
    errors[nameOf((a: Address) => a.province)] = 'validation.error.required';
    errors[nameOf((a: Address) => a.postalCode)] = 'validation.error.required';
    errors[nameOf((a: Address) => a.country)] = 'validation.error.required';
    return errors;
  }

  private isValidCountry(value: string): boolean {
    // Validate country is equal to default value
    return !isNil(value) && value.toLowerCase() === DEFAULT_COUNTRY;
  }

  private validateContent(value: Address): ErrorMessage {
    const errors: ErrorMessage = {};

    errors[nameOf((a: Address) => a.streetNumber)] = this.streetNumberValidator.validate(value.streetNumber);

    errors[nameOf((a: Address) => a.streetName)] = this.streetNameValidator.validate(value.streetName);

    errors[nameOf((a: Address) => a.city)] = this.cityValidator.validate(value.city);

    errors[nameOf((a: Address) => a.unit)] = this.unitValidator.validate(value.unit);

    errors[nameOf((a: Address) => a.province)] = this.provinceValidator.validate(value.province);

    errors[nameOf((a: Address) => a.postalCode)] = this.postalCodeValidator.validate(value.postalCode);

    if (!this.isValidCountry(value.country)) {
      errors[nameOf((a: Address) => a.country)] = 'validation.error.countryInvalid';
    }

    // Coordinates to validate if defined
    if (!isNil(value.coordinates)) {
      if (!isNil(value.coordinates.longitude) && !isNil(value.coordinates.latitude)) {
        errors[nameOf((a: Coordinates) => a.latitude)] = this.latitudeValidator.validate(value.coordinates.latitude);
        errors[nameOf((a: Coordinates) => a.longitude)] = this.longitudeValidator.validate(value.coordinates.longitude);
      }
    }

    return errors;
  }
}
